/**
 * AI Search Enhancement Helper
 * Integrates with Railway backend to enhance search terms
 * Domain: wearr-search-helper-production.up.railway.app
 */
const AISearchHelper = {
  endpoint: 'https://wearr-search-helper-production.up.railway.app/parse-search',
  timeout: 5000, // 5 second timeout (Railway backend needs time to respond)
  cache: new Map(),
  cacheTTL: 300000, // 5 minutes client-side cache
  pendingRequests: new Map(), // Track in-flight requests to prevent duplicates
  currentController: null, // Track current AbortController to cancel old requests

  /**
   * Enhance search term via Railway backend
   * @param {string} searchTerm - Original user input
   * @returns {Promise<string>} Enhanced keywords for Shopify API
   */
  async enhance(searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return searchTerm;
    }

    const cleaned = searchTerm.trim();
    
    // Check local cache first for instant results
    const cacheKey = cleaned.toLowerCase();
    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < this.cacheTTL)) {
      this._syncDates(cached.payload.dates);
      return cached.payload;
    }

    // Check if there's already a pending request for this term
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create the request promise
    const requestPromise = this._fetchEnhancement(cleaned, cacheKey);
    
    // Store it so other concurrent calls can reuse it
    this.pendingRequests.set(cacheKey, requestPromise);
    
    // Clean up after the request completes (success or failure)
    requestPromise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });

    return requestPromise;
  },

  async _fetchEnhancement(cleaned, cacheKey) {
    try {
      // Cancel any previous in-flight request
      if (this.currentController) {
        this.currentController.abort('New search initiated');
      }

      // Create new controller for this request
      const controller = new AbortController();
      this.currentController = controller;
      
      const timeoutId = setTimeout(() => {
        controller.abort('Request timeout');
        this.currentController = null;
      }, this.timeout);

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ searchTerm: cleaned }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      this.currentController = null; // Request completed

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      const normalized = this._normalizeResponse(data, cleaned);
      this._syncDates(normalized.dates);
      
      this.cache.set(cacheKey, {
        payload: normalized,
        timestamp: Date.now()
      });

      // Limit cache size
      if (this.cache.size > 100) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }

      return normalized;

    } catch (error) {
      // Graceful fallback to original search term
      return this._normalizeResponse(null, cleaned);
    }
  },

  /**
   * Clear the client-side cache
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  },

  _normalizeResponse(data, fallbackTerm) {
    const keywords = (data && data.keywords) ? data.keywords : fallbackTerm;
    const original = (data && data.original) ? data.original : fallbackTerm;
    const sizes = Array.isArray(data?.sizes) ? data.sizes.filter(Boolean) : [];
    const colors = Array.isArray(data?.colors) ? data.colors.filter(Boolean) : [];
    const dates = data?.['scaf-dates'] || null;

    return {
      keywords,
      original,
      sizes,
      colors,
      dates
    };
  },

  _syncDates(datePayload) {
    try {
      if (datePayload) {
        sessionStorage.setItem('scaf-dates', JSON.stringify(datePayload));
      } else {
        sessionStorage.removeItem('scaf-dates');
      }
    } catch (error) {
      // Silent fail
    }
  }
};

// Make it available globally
window.AISearchHelper = AISearchHelper;

